import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Minus, Plus, ShieldCheck, Truck, RotateCcw } from "lucide-react";
import { COLORS, PRODUCT, formatPrice, galleryFor, getColor } from "../data/catalog";
import { useCart } from "../context/CartContext";
import { Gallery } from "../components/product/Gallery";
import { Details } from "../components/product/Details";
import { StickyATC } from "../components/product/StickyATC";
import { Swatches } from "../components/site/Swatches";
import { ProductCard } from "../components/site/ProductCard";
import { QuickView } from "../components/site/QuickView";
import { FadeUp } from "../components/site/Reveal";

export default function Product() {
  const [params, setParams] = useSearchParams();
  const color = getColor(params.get("color"));
  const items = galleryFor(color);
  const [idx, setIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [sticky, setSticky] = useState(false);
  const [qv, setQv] = useState(null);
  const atcRef = useRef(null);
  const { add, setOpen } = useCart();
  const nav = useNavigate();

  useEffect(() => { setIdx(0); }, [color.id]);
  useEffect(() => {
    const el = atcRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => setSticky(!e.isIntersecting && e.boundingClientRect.top < 0));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const addToBag = (n = qty) => { add(color.id, n); toast.success(`${PRODUCT.name} — ${color.name} added to your bag`); setOpen(true); };
  const buyNow = () => { add(color.id, qty); nav("/checkout"); };

  return (
    <div className="bg-ivory pb-24 pt-32 md:pt-40" data-testid="product-page">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <nav className="mb-6 font-mono text-[11px] uppercase tracking-[0.2em] text-graphite" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-ink">Home</Link> / <Link to="/shop/belt-bags" className="hover:text-ink">Belt Bags</Link> / <span className="text-ink">{color.name}</span>
        </nav>
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="min-w-0 lg:col-span-7"><Gallery items={items} idx={idx} setIdx={setIdx} colorName={color.name} /></div>
          <div className="min-w-0 lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <p className="label-mono">Belt Bag · Crossbody · New</p>
              <h1 className="mt-3 font-display text-4xl font-light leading-[1] text-ink sm:text-5xl lg:text-6xl" data-testid="pdp-title">{PRODUCT.name}</h1>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-xl text-ink" data-testid="pdp-price">{formatPrice(PRODUCT.price)}</span>
              </div>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-graphite">SKU <span className="text-ink" data-testid="pdp-sku">{color.sku}</span></p>
              <p className="mt-5 text-sm leading-relaxed text-graphite">{PRODUCT.description}</p>

              <div className="mt-8">
                <p className="mb-3 text-sm text-graphite">Color: <AnimatePresence mode="popLayout" initial={false}><motion.span key={color.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="inline-block text-ink" data-testid="pdp-color-name">{color.name}</motion.span></AnimatePresence></p>
                <Swatches value={color.id} onChange={(id) => setParams({ color: id }, { replace: true, preventScrollReset: true })} prefix="pdp" size="lg" />
              </div>

              <div className="mt-8 flex gap-3" ref={atcRef}>
                <div className="flex items-center border border-line">
                  <button aria-label="Decrease quantity" data-testid="pdp-qty-dec" onClick={() => setQty(Math.max(1, qty - 1))} className="grid h-full w-11 place-items-center"><Minus className="h-3.5 w-3.5" /></button>
                  <span className="w-8 text-center text-sm" data-testid="pdp-qty">{qty}</span>
                  <button aria-label="Increase quantity" data-testid="pdp-qty-inc" onClick={() => setQty(qty + 1)} className="grid h-full w-11 place-items-center"><Plus className="h-3.5 w-3.5" /></button>
                </div>
                <button onClick={() => addToBag()} data-testid="pdp-add-to-cart" className="btn-dark flex-1">Add to bag</button>
              </div>
              <button onClick={buyNow} data-testid="pdp-buy-now" className="btn-outline mt-3 w-full">Buy it now</button>

              <ul className="mt-8 grid grid-cols-3 gap-3 text-center text-[11px] text-graphite">
                {[[ShieldCheck, "Secure checkout"], [Truck, "Free U.S. shipping $75+"], [RotateCcw, "30-day returns"]].map(([I, t]) => (
                  <li key={t} className="flex flex-col items-center gap-2 border border-line px-2 py-4"><I className="h-4 w-4 text-ink" />{t}</li>
                ))}
              </ul>
              <div className="mt-8"><Details /></div>
            </div>
          </div>
        </div>

        <section className="mt-28" data-testid="pdp-related">
          <FadeUp><p className="label-mono">You may also like</p><h2 className="mt-3 font-display text-4xl font-light text-ink md:text-5xl">Complete your <em>palette.</em></h2></FadeUp>
          <div className="mt-10 grid grid-cols-2 gap-x-3 gap-y-10 md:gap-x-6 lg:grid-cols-5">
            {COLORS.filter((c) => c.id !== color.id).map((c) => <ProductCard key={c.id} colorId={c.id} onQuickView={setQv} prefix="related" />)}
          </div>
        </section>
      </div>
      <StickyATC show={sticky} color={color} onAdd={() => addToBag(1)} />
      <QuickView colorId={qv} onClose={() => setQv(null)} />
    </div>
  );
}
